// Helpers de exportação de relatórios — PDF (window.print) + Excel (SheetJS)
// Mantém o padrão visual do sistema e respeita os filtros aplicados.
import * as XLSX from "xlsx";

const brl = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" })
  .format(Number(n || 0));

const fmtDateTime = (d = new Date()) =>
  d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

const safeFilename = (s) => (s || "relatorio")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-zA-Z0-9-_]/g, "_").toLowerCase();

// ─────────────────────────────────────────────────────────────────────────────
// Export PDF: monta HTML profissional + abre janela + dispara print.
// IMPORTANTE: aceita `popupWindow` quando o caller já abriu a janela dentro
// do click handler (evita popup blocker em produção). Se não for passado,
// abre aqui (funciona em dev/preview).
// ─────────────────────────────────────────────────────────────────────────────
export function exportPDF({ filename, title, subtitle, store, bodyHtml, popupWindow }) {
  const logo = store?.logo ? `<img src="${store.logo}" alt="logo"/>` : "";
  const storeName = store?.name || "Loja";
  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${title}</title>
<style>
*{box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,sans-serif;color:#111;padding:24px;background:#fff}
.head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0A0A0A;padding-bottom:10px;margin-bottom:14px}
.head img{max-height:56px;max-width:140px;object-fit:contain}
.head .meta{text-align:right;font-size:11px;color:#666}
.head .meta b{display:block;color:#0A0A0A;font-size:14px;text-transform:uppercase;letter-spacing:0.04em}
h1{font-size:20px;margin:0 0 2px}
.sub{color:#666;font-size:11px;margin:0 0 14px}
.cards{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:14px 0}
.card{border:1px solid #ddd;border-radius:6px;padding:8px}
.card h3{font-size:9px;text-transform:uppercase;color:#888;margin:0 0 4px;letter-spacing:.04em}
.card .v{font-size:16px;font-weight:bold}
table{width:100%;border-collapse:collapse;font-size:10px;margin-top:4px}
th,td{padding:5px 6px;text-align:left;border-bottom:1px solid #eee;vertical-align:top}
th{background:#f5f5f5;text-transform:uppercase;font-size:9px;color:#555;letter-spacing:.04em}
.r{text-align:right;white-space:nowrap}
.c{text-align:center}
.muted{color:#888;font-size:9px}
.totals{margin-top:14px;border-top:2px solid #0A0A0A;padding-top:8px}
.totals .row{display:flex;justify-content:space-between;font-size:12px;padding:3px 0}
.totals .row.b{font-weight:bold;font-size:14px;border-top:1px solid #ccc;padding-top:6px;margin-top:6px}
.section-title{font-size:13px;margin:18px 0 4px;font-weight:bold;text-transform:uppercase;letter-spacing:.04em}
.paid{color:#047857;font-weight:600}
.overdue{color:#b91c1c;font-weight:600}
.pending{color:#b45309}
@media print{body{padding:14px}.no-print{display:none!important}}
.footer{margin-top:24px;border-top:1px solid #ddd;padding-top:8px;font-size:9px;color:#888;text-align:center}
</style></head><body>
<div class="head">
  <div style="display:flex;gap:10px;align-items:center">${logo}<div><h1>${title}</h1>${subtitle ? `<div class="sub">${subtitle}</div>` : ""}</div></div>
  <div class="meta"><b>${storeName}</b>${store?.phone ? `<div>${store.phone}</div>` : ""}<div>Emitido em ${fmtDateTime()}</div></div>
</div>
${bodyHtml}
<div class="footer">Relatório gerado pelo Gestão Expert · ${fmtDateTime()}</div>
</body></html>`;
  const w = popupWindow || window.open("", "_blank");
  if (!w) {
    alert("Permita popups para exportar o PDF.");
    return;
  }
  // Se a janela já estava aberta (modo loader), limpa antes de escrever
  try { w.document.open(); } catch {}
  w.document.write(html);
  w.document.close();
  // Aguarda logo (se houver) carregar antes de disparar o print pra não
  // sair página sem logo em alguns Chrome/Edge.
  const triggerPrint = () => {
    try { w.focus(); w.print(); } catch {}
  };
  try {
    const imgs = w.document.images || [];
    if (imgs.length === 0) {
      setTimeout(triggerPrint, 150);
    } else {
      let pending = imgs.length;
      const done = () => { pending--; if (pending <= 0) setTimeout(triggerPrint, 80); };
      for (let i = 0; i < imgs.length; i++) {
        if (imgs[i].complete) done();
        else { imgs[i].onload = done; imgs[i].onerror = done; }
      }
      // Fallback caso onload nunca dispare (CORS/lentidão extrema)
      setTimeout(triggerPrint, 2500);
    }
  } catch {
    setTimeout(triggerPrint, 600);
  }
  // ignore filename here — o usuário escolhe ao salvar via diálogo nativo do PDF
  return filename;
}

// ─────────────────────────────────────────────────────────────────────────────
// Export Excel (xlsx) — múltiplas sheets, colunas auto-ajustadas.
// `sheets`: [{ name: "Sheet1", data: [{col:val,...},...], colWidths?: [n,...] }]
// Se sheet tem `header`, esse array de strings vira primeira linha "merge title".
// ─────────────────────────────────────────────────────────────────────────────
export function exportExcel({ filename, sheets }) {
  const wb = XLSX.utils.book_new();
  sheets.forEach((sh, idx) => {
    const data = sh.data || [];
    const ws = XLSX.utils.json_to_sheet(data);
    // Auto-fit columns (largura baseada no maior valor por coluna)
    if (data.length) {
      const keys = Object.keys(data[0]);
      const cols = keys.map((k) => {
        const maxLen = Math.max(
          String(k).length,
          ...data.map((r) => String(r[k] == null ? "" : r[k]).length)
        );
        return { wch: Math.min(60, Math.max(8, maxLen + 2)) };
      });
      ws["!cols"] = cols;
    }
    const name = (sh.name || `Sheet${idx + 1}`).slice(0, 31).replace(/[\/?*[\]:]/g, "_");
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, `${safeFilename(filename)}.xlsx`);
}

export { brl, fmtDateTime, safeFilename };
